/** @jsxImportSource @emotion/react */
'use memo'

import { useEffect, useRef } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { DivisionTeam } from '@sportninja/common/features/registration/startStep/types'
import type { Division } from '@sportninja/common/types'
import Text from 'src/components/ts-components/Text'
import { teamIdentity } from '@sportninja/common/features/registration/startStep/teamDisplay'
import TeamCard from './TeamCard'

interface Props {
  results: DivisionTeam<Division>[]
  selectedTeamId?: string | null
  onSelect: (entry: DivisionTeam<Division>) => void
  /** Whether the reader is refused by a team — the step's one rule (`isTeamFullFor`). */
  isTeamFull: (entry: DivisionTeam<Division>) => boolean
  /** Set while a division is chosen. Only used to drop the per-card division
   * name — the "showing teams in X" line itself is rendered by EntityZone, on
   * the search row. */
  scopedDivisionName?: string | null
  hasQuery: boolean
  /**
   * Set when the selection was made somewhere other than this list — a deep
   * link, or a return to this tab — so the chosen team can be scrolled back
   * into view instead of leaving the reader at the top of a long list.
   */
  scrollToSelectionKey?: number
}

/**
 * Search results for the teams tab.
 *
 * Each row names the division its team sits in, so the division that selecting
 * it will imply is visible before the reader commits.
 *
 * A row shows only name, image and division — that is everything the nested
 * `division.teams[]` carries. In particular there is no roster capacity here,
 * so a row cannot say whether a team is full; that is only knowable later.
 */
const TeamsTab = ({
  results,
  selectedTeamId,
  onSelect,
  isTeamFull,
  scopedDivisionName,
  hasQuery,
  scrollToSelectionKey,
}: Props) => {
  const selectedRef = useRef<HTMLDivElement | null>(null)

  // Two tabs are two views of one answer, so arriving here with the answer
  // already made must show it rather than the top of the list.
  useEffect(() => {
    if (!scrollToSelectionKey || !selectedRef.current) {
      return
    }
    selectedRef.current.scrollIntoView({ block: 'nearest' })
  }, [scrollToSelectionKey, selectedTeamId])

  return (
    <div css={wrapStyle}>
      {results.length === 0 ? (
        <div css={emptyStyle}>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_40}
          >
            {capitalizeWords(
              hasQuery
                ? 'No teams match that search.'
                : 'No teams have registered yet.'
            )}
          </Text>
        </div>
      ) : (
        <div css={gridStyle}>
          {results.map(({ team, division }) => {
            const teamId = teamIdentity(team)
            const isSelected = Boolean(teamId) && selectedTeamId === teamId
            return (
              <div
                key={teamId}
                ref={isSelected ? selectedRef : undefined}
                css={cellStyle}
              >
                <TeamCard
                  team={team}
                  // Redundant when the whole list is already scoped to one
                  // division — it is stated once above instead of on every card.
                  divisionName={scopedDivisionName ? null : division.name}
                  isSelected={isSelected}
                  hasSelection={Boolean(selectedTeamId)}
                  isFull={isTeamFull({ team, division })}
                  onSelect={() => onSelect({ team, division })}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TeamsTab

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

/* Three across on desktop, matching the divisions grid — the two tabs answer
   the same question, so they should not look like different screens. */
/* The scroll-target wrapper is the grid item, so it has to pass the grid's
   stretch through to the card rather than letting it collapse. */
const cellStyle = css`
  display: flex;

  > * {
    height: 100%;
  }
`

const gridStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  align-items: stretch;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const emptyStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 32px 16px;
  border: 1px dashed ${colors.NEUTRAL_600};
  border-radius: 8px;
  text-align: center;
`
